import {
    type MemberInput,
    RetirementAccountType,
    type RetirementContributionWaterfall,
} from './types';

// Given a set of retirement accounts, generates a prioritized list of the retirement account types (the "waterfall") along with percent allocations for that retirement account type as Roth vs Non-Roth, when applicable.
export class RetirementContributionWaterfallGenerator {
    constructor(
        private readonly currentMarginalTaxRate: number,
        private readonly memberInputs: MemberInput[],
    ) {}

    public generate(): RetirementContributionWaterfall {
        const waterfall: RetirementContributionWaterfall = [];

        const rothPercentage = this.getRothPercentage();

        // 1. employer-sponsored plans
        this.memberInputs.forEach((member) => {
            if (member.hasEmployerRetirementPlan) {
                waterfall.push({
                    memberId: member.id,
                    contributions: {
                        [RetirementAccountType.EMPLOYER_401K]:
                            1 - rothPercentage,
                        [RetirementAccountType.ROTH_401K]: rothPercentage,
                    },
                });
            }
        });

        // 2. self-employed plans
        // 2a. SEP IRA
        // 2b. Solo 401k
        this.memberInputs.forEach((member) => {
            if (member.isSelfEmployed) {
                waterfall.push(
                    {
                        memberId: member.id,
                        contributions: {
                            [RetirementAccountType.SEP_IRA]: 1,
                        },
                    },
                    {
                        memberId: member.id,
                        contributions: {
                            [RetirementAccountType.SOLO_401K]: 1,
                        },
                    },
                );
            }
        });

        // 3. IRA
        this.memberInputs.forEach((member) => {
            if (member.currentGrossAnnualIncome > 0) {
                waterfall.push({
                    memberId: member.id,
                    contributions: {
                        [RetirementAccountType.ROTH_IRA]: rothPercentage,
                        [RetirementAccountType.TRAD_IRA]: 1 - rothPercentage,
                    },
                });
            }
        });

        // 4. backdoor Roth IRA
        this.memberInputs.forEach((member) => {
            if (
                member.currentRetirementSavings.every(
                    ({ type }) => type !== RetirementAccountType.TRAD_IRA,
                )
            ) {
                waterfall.push({
                    memberId: member.id,
                    contributions: {
                        [RetirementAccountType.BACKDOOR_ROTH_IRA]: 1,
                    },
                });
            }
        });

        // 5. taxable brokerage
        this.memberInputs.forEach((member) => {
            waterfall.push({
                memberId: member.id,
                contributions: {
                    [RetirementAccountType.TAXABLE_BROKERAGE]: 1,
                },
            });
        });

        return waterfall;
    }

    private getRothPercentage(): number {
        if (this.currentMarginalTaxRate < 0.25) {
            return 1.0;
        } else if (this.currentMarginalTaxRate < 0.32) {
            return 0.5;
        } else {
            return 0.0;
        }
    }
}
